import dayjs from 'dayjs';
import { calculateDateRange } from '../../utils/dates';
import { exportCohortUsersToCSV, exportSessionEventsToCSV } from '../../utils/exportUtils';
import {
    mapAnalyticsMetricsToChartRows,
    USER_ADOPTION_FIELD_MAP,
    ENGAGEMENT_SUMMARY_FIELD_MAP,
} from '../../utils/analyticsMetrics';
import {
    USER_SUMMARY_PAGE_SIZE,
    DEFAULT_USER_SUMMARY,
    DEFAULT_TRANSACTION_SUMMARY,
    DEFAULT_SESSIONS_DRAWER,
    DEFAULT_USER_ADOPTION_METRICS,
    DEFAULT_ENGAGEMENT_SUMMARY_METRICS,
    DEFAULT_COHORT_MODAL,
} from './dashboard.constants';

// Parse a users.updated_at value to epoch ms for sorting; null when
// empty/unparseable so those rows can be pushed to the bottom.
function toLastActiveTimestamp(value) {
    if (value == null || value === '') return null;
    const raw = String(value);
    if (/^\d+$/.test(raw)) {
        return raw.length >= 13 ? Number(raw) : Number(raw) * 1000;
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
}

/**
 * State-only helpers for the Dashboard: resets, derivations, and event handlers
 * that mutate state or orchestrate loads. All methods that call `this.data.*`
 * live in dashboard.controller.js — this mixin never fetches.
 */
class DashboardDelegate {
    resetUserSummary() {
        this._userSummaryInflightKey = null;
        this.setState({ userSummary: { ...DEFAULT_USER_SUMMARY } });
    }

    resetTransactionSummary() {
        this._transactionSummaryInflightKey = null;
        this.setState({ transactionSummary: { ...DEFAULT_TRANSACTION_SUMMARY } });
    }

    // --- Enrollment funnel (separate endpoint; all-time, loaded once) ---

    /** Load the enrollment funnel on its own. All-time, so it's fetched once. */
    async loadFunnel() {
        if (this.state.funnel?.steps?.length || this._funnelInflight) return;
        this._funnelInflight = true;
        this.setState((prev) => ({ funnel: { steps: prev.funnel?.steps || [], loading: true } }));
        try {
            const payload = await this.data.fetchDashboardFunnel();
            this.setState({ funnel: { steps: payload?.steps || [], loading: false } });
        } catch (error) {
            console.error('Error loading funnel', error);
            this.setState((prev) => ({ funnel: { steps: prev.funnel?.steps || [], loading: false } }));
        } finally {
            this._funnelInflight = false;
        }
    }

    resetUserAdoptionMetrics() {
        this._userAdoptionInflightKey = null;
        this.setState({ userAdoptionMetrics: { ...DEFAULT_USER_ADOPTION_METRICS } });
    }

    // --- Adoption card drill-down (Opt-In / Enrolled / Active user lists) ---

    /** Open the modal for a metric and kick off its (lazy) load. */
    openCohortModal(metric) {
        if (!metric) return;
        this.setState(
            { cohortModal: { ...DEFAULT_COHORT_MODAL, open: true, metric, loading: true } },
            () => this.loadCohortUsers()
        );
    }

    closeCohortModal() {
        this._cohortInflightKey = null;
        this.setState({ cohortModal: { ...DEFAULT_COHORT_MODAL } });
    }

    async loadCohortUsers() {
        const { timeFrame, dateRange, cohortModal = DEFAULT_COHORT_MODAL } = this.state;
        const metric = cohortModal.metric;
        if (!metric) return;

        const { start, end } = calculateDateRange(timeFrame, dateRange);
        const requestKey = `${metric}|${start}|${end}`;
        this._cohortInflightKey = requestKey;

        try {
            const payload = await this.data.fetchDashboardCohortUsers({ metric, start, end });
            if (this._cohortInflightKey !== requestKey) return;
            const users = payload?.users || [];
            this.setState((prev) => ({
                cohortModal: {
                    ...prev.cohortModal,
                    users,
                    count: payload?.count ?? users.length,
                    loading: false,
                },
            }));
        } catch (error) {
            console.error('Error loading cohort users', error);
            if (this._cohortInflightKey !== requestKey) return;
            this.setState((prev) => ({
                cohortModal: { ...prev.cohortModal, users: [], count: 0, loading: false },
            }));
        } finally {
            if (this._cohortInflightKey === requestKey) {
                this._cohortInflightKey = null;
            }
        }
    }

    handleCohortSearch(query) {
        this.setState((prev) => ({ cohortModal: { ...prev.cohortModal, query } }));
    }

    handleCohortSort(sortBy) {
        this.setState((prev) => ({ cohortModal: { ...prev.cohortModal, sortBy } }));
    }

    exportCohortUsers() {
        exportCohortUsersToCSV(this.state.cohortModal?.users || []);
    }

    // --- Total Sessions drill-down drawer (Usage tab) ---

    /** Open the sessions drawer and load its (lazy) event list. */
    openSessionsDrawer() {
        this.setState(
            { sessionsDrawer: { ...DEFAULT_SESSIONS_DRAWER, open: true, loading: true } },
            () => this.loadSessionEvents()
        );
    }

    closeSessionsDrawer() {
        this._sessionsInflightKey = null;
        this.setState({ sessionsDrawer: { ...DEFAULT_SESSIONS_DRAWER } });
    }

    async loadSessionEvents() {
        const { timeFrame, dateRange } = this.state;
        const { start, end } = calculateDateRange(timeFrame, dateRange);

        const requestKey = `${start}|${end}`;
        this._sessionsInflightKey = requestKey;

        try {
            const payload = await this.data.fetchDashboardSessionEvents({ start, end });
            if (this._sessionsInflightKey !== requestKey) return;
            const events = payload?.events || [];
            this.setState((prev) => ({
                sessionsDrawer: {
                    ...prev.sessionsDrawer,
                    events,
                    count: payload?.count ?? events.length,
                    loading: false,
                },
            }));
        } catch (error) {
            console.error('Error loading session events', error);
            if (this._sessionsInflightKey !== requestKey) return;
            this.setState((prev) => ({
                sessionsDrawer: { ...prev.sessionsDrawer, events: [], count: 0, loading: false },
            }));
        } finally {
            if (this._sessionsInflightKey === requestKey) {
                this._sessionsInflightKey = null;
            }
        }
    }

    handleSessionsSearch(query) {
        this.setState((prev) => ({ sessionsDrawer: { ...prev.sessionsDrawer, query } }));
    }

    handleSessionsPlatform(platform) {
        this.setState((prev) => ({ sessionsDrawer: { ...prev.sessionsDrawer, platform } }));
    }

    handleSessionsType(type) {
        this.setState((prev) => ({ sessionsDrawer: { ...prev.sessionsDrawer, type } }));
    }

    handleSessionsDateRange(dateRange) {
        this.setState((prev) => ({ sessionsDrawer: { ...prev.sessionsDrawer, dateRange } }));
    }

    /** Derive the display rows (filtered, formatted, keyed) for the drawer table. */
    getSessionRows() {
        const {
            events = [],
            query = '',
            platform = 'all',
            type = 'all',
            dateRange = null,
        } = this.state.sessionsDrawer || {};

        const q = query.trim().toLowerCase();
        let rows = events;

        if (q) {
            rows = rows.filter(
                (e) =>
                    (e.name || '').toLowerCase().includes(q) ||
                    (e.user_id || '').toLowerCase().includes(q)
            );
        }
        if (platform !== 'all') {
            rows = rows.filter((e) => (e.platform || 'Unknown') === platform);
        }
        if (type !== 'all') {
            rows = rows.filter((e) => (type === 'new' ? e.is_new : !e.is_new));
        }
        if (dateRange && dateRange[0] && dateRange[1]) {
            const startMs = dateRange[0].startOf('day').valueOf();
            const endMs = dateRange[1].endOf('day').valueOf();
            rows = rows.filter(
                (e) => e.timestamp_ms != null && e.timestamp_ms >= startMs && e.timestamp_ms <= endMs
            );
        }

        return rows.map((e, i) => ({
            key: e.user_id ? `${e.user_id}-${e.timestamp_ms}-${i}` : `session-${i}`,
            name: e.name,
            user_id: e.user_id,
            timestamp_ms: e.timestamp_ms,
            date: e.timestamp_ms ? dayjs(e.timestamp_ms).format('MMM D, YYYY · h:mm A') : '—',
            platform: e.platform,
            isNew: e.is_new,
        }));
    }

    /** Distinct platform values present in the loaded events (for the filter). */
    getSessionPlatformOptions() {
        const events = this.state.sessionsDrawer?.events || [];
        const seen = new Set();
        events.forEach((e) => seen.add(e.platform || 'Unknown'));
        return Array.from(seen).sort();
    }

    exportSessionEvents() {
        exportSessionEventsToCSV(this.getSessionRows());
    }

    /** Derive the display rows (search-filtered, sorted, keyed) for the modal. */
    getCohortRows() {
        const { users = [], query = '', sortBy = 'name' } = this.state.cohortModal || {};

        const q = query.trim().toLowerCase();
        const filtered = q
            ? users.filter(
                  (u) =>
                      (u.name || '').toLowerCase().includes(q) ||
                      (u.user_id || '').toLowerCase().includes(q)
              )
            : users;

        let ordered = filtered;
        if (sortBy === 'last_active_desc' || sortBy === 'last_active_asc') {
            const dir = sortBy === 'last_active_desc' ? -1 : 1;
            ordered = [...filtered].sort((a, b) => {
                const ta = toLastActiveTimestamp(a.last_active);
                const tb = toLastActiveTimestamp(b.last_active);
                if (ta == null && tb == null) return 0;
                if (ta == null) return 1; // unknown last-active always sorts to the bottom
                if (tb == null) return -1;
                return (ta - tb) * dir;
            });
        }

        return ordered.map((u, i) => ({ ...u, key: u.user_id || `row-${i}` }));
    }

    resetEngagementSummaryMetrics() {
        this._engagementSummaryInflightKey = null;
        this.setState({ engagementSummaryMetrics: { ...DEFAULT_ENGAGEMENT_SUMMARY_METRICS } });
    }

    async loadUserSummaryPage(page = 1, pageSize = USER_SUMMARY_PAGE_SIZE, range = null) {
        const { timeFrame, dateRange, userSummary = DEFAULT_USER_SUMMARY } = this.state;
        const { start, end } = range || calculateDateRange(timeFrame, dateRange);
        if (!start || !end) return;

        const requestKey = `${start}|${end}|${page}|${pageSize}`;
        if (this._userSummaryInflightKey === requestKey) return;
        this._userSummaryInflightKey = requestKey;

        this.setState({
            userSummary: {
                ...userSummary,
                loading: true,
            },
        });

        try {
            const payload = await this.data.fetchDashboardUserSummary({
                start,
                end,
                page,
                pageSize,
            });
            const rows = payload?.items || [];
            const pageinfo = payload?.pageinfo || {};
            if (this._userSummaryInflightKey !== requestKey) return;
            this.setState({
                userSummary: {
                    rows,
                    loading: false,
                    pageInfo: {
                        current: pageinfo.page || page,
                        perPage: pageinfo.perPage || pageSize,
                        total: pageinfo.count ?? rows.length,
                    },
                },
            });
        } catch (error) {
            console.error('Error loading user summary', error);
            if (this._userSummaryInflightKey !== requestKey) return;
            this.setState({
                userSummary: {
                    rows: [],
                    loading: false,
                    pageInfo: { current: page, perPage: pageSize, total: 0 },
                },
            });
        } finally {
            if (this._userSummaryInflightKey === requestKey) {
                this._userSummaryInflightKey = null;
            }
        }
    }

    async refreshUserSummaryForPeriod({ start, end }) {
        this.resetUserSummary();
        if (!start || !end) return;
        await this.loadUserSummaryPage(1, USER_SUMMARY_PAGE_SIZE, { start, end });
    }

    async handleUserSummaryTableChange(antdPagination = {}, _filters, _sorter, extra) {
        if (extra?.action && extra.action !== 'paginate') return;
        await this.loadUserSummaryPage(
            antdPagination?.current || 1,
            antdPagination?.pageSize || this.state.userSummary?.pageInfo?.perPage || USER_SUMMARY_PAGE_SIZE
        );
    }

    // --- Usage tab Transaction Summary (advances list) ---

    async loadTransactionSummaryPage(page = 1, pageSize = USER_SUMMARY_PAGE_SIZE, range = null) {
        const { timeFrame, dateRange, transactionSummary = DEFAULT_TRANSACTION_SUMMARY } = this.state;
        const { start, end } = range || calculateDateRange(timeFrame, dateRange);
        if (!start || !end) return;

        const requestKey = `${start}|${end}|${page}|${pageSize}`;
        if (this._transactionSummaryInflightKey === requestKey) return;
        this._transactionSummaryInflightKey = requestKey;

        this.setState({
            transactionSummary: {
                ...transactionSummary,
                loading: true,
            },
        });

        try {
            const payload = await this.data.fetchDashboardTransactionSummary({
                start,
                end,
                page,
                pageSize,
            });
            const rows = payload?.items || [];
            const pageinfo = payload?.pageinfo || {};
            if (this._transactionSummaryInflightKey !== requestKey) return;
            this.setState({
                transactionSummary: {
                    rows,
                    loading: false,
                    pageInfo: {
                        current: pageinfo.page || page,
                        perPage: pageinfo.perPage || pageSize,
                        total: pageinfo.count ?? rows.length,
                    },
                },
            });
        } catch (error) {
            console.error('Error loading transaction summary', error);
            if (this._transactionSummaryInflightKey !== requestKey) return;
            this.setState({
                transactionSummary: {
                    rows: [],
                    loading: false,
                    pageInfo: { current: page, perPage: pageSize, total: 0 },
                },
            });
        } finally {
            if (this._transactionSummaryInflightKey === requestKey) {
                this._transactionSummaryInflightKey = null;
            }
        }
    }

    async refreshTransactionSummaryForPeriod({ start, end }) {
        this.resetTransactionSummary();
        if (!start || !end) return;
        await this.loadTransactionSummaryPage(1, USER_SUMMARY_PAGE_SIZE, { start, end });
    }

    async handleTransactionSummaryTableChange(antdPagination = {}, _filters, _sorter, extra) {
        if (extra?.action && extra.action !== 'paginate') return;
        await this.loadTransactionSummaryPage(
            antdPagination?.current || 1,
            antdPagination?.pageSize || this.state.transactionSummary?.pageInfo?.perPage || USER_SUMMARY_PAGE_SIZE
        );
    }

    async handleUserAdoptionFrequencyChange(frequency) {
        const { timeFrame, dateRange, userAdoptionMetrics } = this.state;
        const { start, end } = calculateDateRange(timeFrame, dateRange);
        if (!start || !end) return;

        if (userAdoptionMetrics?.data?.[frequency] !== undefined) {
            this.setState({
                userAdoptionMetrics: {
                    ...userAdoptionMetrics,
                    activeFrequency: frequency,
                },
            });
            return;
        }

        await this.loadUserAdoptionMetrics(frequency, { start, end });
    }

    async loadUserAdoptionMetrics(frequency, { start, end }) {
        if (!start || !end) return;

        const { timeFrame, dateRange } = this.state;
        const requestKey = `${start}|${end}|${frequency}`;
        const { userAdoptionMetrics = DEFAULT_USER_ADOPTION_METRICS } = this.state;

        if (userAdoptionMetrics.data?.[frequency] !== undefined) {
            this.setState({
                userAdoptionMetrics: {
                    ...userAdoptionMetrics,
                    activeFrequency: frequency,
                },
            });
            return;
        }

        if (this._userAdoptionInflightKey === requestKey) {
            return;
        }
        this._userAdoptionInflightKey = requestKey;

        this.setState({
            userAdoptionMetrics: {
                ...userAdoptionMetrics,
                activeFrequency: frequency,
                loading: true,
            },
        });

        try {
            const payload = await this.data.fetchAnalyticsMetrics({
                timeFrame,
                dateRange,
                interval: frequency,
            });
            const metricsRows = payload?.data ?? [];
            const rows = mapAnalyticsMetricsToChartRows(
                metricsRows,
                frequency,
                USER_ADOPTION_FIELD_MAP
            );

            if (this._userAdoptionInflightKey !== requestKey) return;
            this.setState((prev) => ({
                userAdoptionMetrics: {
                    activeFrequency: frequency,
                    loading: false,
                    data: {
                        ...prev.userAdoptionMetrics?.data,
                        [frequency]: rows,
                    },
                },
            }));
        } catch (error) {
            console.error('Error loading user adoption metrics', error);
            if (this._userAdoptionInflightKey !== requestKey) return;
            this.setState((prev) => ({
                userAdoptionMetrics: {
                    activeFrequency: frequency,
                    loading: false,
                    data: {
                        ...prev.userAdoptionMetrics?.data,
                        [frequency]: [],
                    },
                },
            }));
        } finally {
            if (this._userAdoptionInflightKey === requestKey) {
                this._userAdoptionInflightKey = null;
            }
        }
    }

    async refreshUserAdoptionMetricsForPeriod({ start, end }) {
        this.resetUserAdoptionMetrics();
        if (!start || !end) return;
        await this.loadUserAdoptionMetrics('monthly', { start, end });
    }

    async ensureUserAdoptionMetricsLoaded() {
        const { timeFrame, dateRange, userAdoptionMetrics } = this.state;
        const { start, end } = calculateDateRange(timeFrame, dateRange);
        if (!start || !end) return;

        const frequency = userAdoptionMetrics?.activeFrequency ?? 'monthly';
        if (userAdoptionMetrics?.data?.[frequency] !== undefined) return;

        await this.loadUserAdoptionMetrics(frequency, { start, end });
    }

    async handleEngagementSummaryFrequencyChange(frequency) {
        const { timeFrame, dateRange, engagementSummaryMetrics } = this.state;
        const { start, end } = calculateDateRange(timeFrame, dateRange);
        if (!start || !end) return;

        if (engagementSummaryMetrics?.data?.[frequency] !== undefined) {
            this.setState({
                engagementSummaryMetrics: {
                    ...engagementSummaryMetrics,
                    activeFrequency: frequency,
                },
            });
            return;
        }

        await this.loadEngagementSummaryMetrics(frequency, { start, end });
    }

    async loadEngagementSummaryMetrics(frequency, { start, end }) {
        if (!start || !end) return;

        const { timeFrame, dateRange } = this.state;
        const requestKey = `${start}|${end}|${frequency}`;
        const { engagementSummaryMetrics = DEFAULT_ENGAGEMENT_SUMMARY_METRICS } = this.state;

        if (engagementSummaryMetrics.data?.[frequency] !== undefined) {
            this.setState({
                engagementSummaryMetrics: {
                    ...engagementSummaryMetrics,
                    activeFrequency: frequency,
                },
            });
            return;
        }

        if (this._engagementSummaryInflightKey === requestKey) return;
        this._engagementSummaryInflightKey = requestKey;

        this.setState({
            engagementSummaryMetrics: {
                ...engagementSummaryMetrics,
                activeFrequency: frequency,
                loading: true,
            },
        });

        try {
            const payload = await this.data.fetchAnalyticsMetrics({
                timeFrame,
                dateRange,
                interval: frequency,
            });
            const metricsRows = payload?.data ?? [];
            // Engaged is a direct metric from the API (unique Session
            // Authenticated users per bucket), not derived from enrolled − active.
            const rows = mapAnalyticsMetricsToChartRows(
                metricsRows,
                frequency,
                ENGAGEMENT_SUMMARY_FIELD_MAP
            );

            if (this._engagementSummaryInflightKey !== requestKey) return;
            this.setState((prev) => ({
                engagementSummaryMetrics: {
                    activeFrequency: frequency,
                    loading: false,
                    data: {
                        ...prev.engagementSummaryMetrics?.data,
                        [frequency]: rows,
                    },
                },
            }));
        } catch (error) {
            console.error('Error loading engagement summary metrics', error);
            if (this._engagementSummaryInflightKey !== requestKey) return;
            this.setState((prev) => ({
                engagementSummaryMetrics: {
                    activeFrequency: frequency,
                    loading: false,
                    data: {
                        ...prev.engagementSummaryMetrics?.data,
                        [frequency]: [],
                    },
                },
            }));
        } finally {
            if (this._engagementSummaryInflightKey === requestKey) {
                this._engagementSummaryInflightKey = null;
            }
        }
    }

    async refreshEngagementSummaryMetricsForPeriod({ start, end }) {
        this.resetEngagementSummaryMetrics();
        if (!start || !end) return;
        await this.loadEngagementSummaryMetrics('monthly', { start, end });
    }

    async ensureEngagementSummaryMetricsLoaded() {
        const { timeFrame, dateRange, engagementSummaryMetrics } = this.state;
        const { start, end } = calculateDateRange(timeFrame, dateRange);
        if (!start || !end) return;

        const frequency = engagementSummaryMetrics?.activeFrequency ?? 'monthly';
        if (engagementSummaryMetrics?.data?.[frequency] !== undefined) return;

        await this.loadEngagementSummaryMetrics(frequency, { start, end });
    }
}

export default DashboardDelegate;
